import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';

const HolidayCalendar = ({ userId, showTeamLeaves = false }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [holidays, setHolidays] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, [currentDate, userId, showTeamLeaves]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const year = currentDate.getFullYear();

            // Fetch holidays
            const holidayRes = await api.get(`/holidays?year=${year}`);
            setHolidays(holidayRes.data);

            // Fetch leaves
            const leaveRequests = [];
            
            // 1. Always fetch my leaves
            const myLeavesRes = await api.get('/leaves/my');
            leaveRequests.push(...myLeavesRes.data);
            
            // 2. Fetch team leaves if requested
            if (showTeamLeaves) {
                const teamLeavesRes = await api.get('/leaves/team');
                leaveRequests.push(...teamLeavesRes.data);
            }
            
            // Filter only approved and unique leaves
            const uniqueApproved = Array.from(new Map(
                leaveRequests
                    .filter(l => l.status === 'APPROVED')
                    .map(l => [l.id, l])
            ).values());
            
            setLeaves(uniqueApproved);
        } catch (error) {
            console.error('Error fetching calendar data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        return { daysInMonth, startingDayOfWeek, year, month };
    };

    const isHoliday = (date) => {
        return holidays.find(h => {
            const hDate = new Date(h.date);
            return hDate.getUTCDate() === date.getDate() &&
                hDate.getUTCMonth() === date.getMonth() &&
                hDate.getUTCFullYear() === date.getFullYear();
        });
    };

    const hasLeave = (date) => {
        const targetTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        
        return leaves.filter(l => {
            // Server dates are stored as UTC midnight. 
            // We map them to local time components for comparison with the calendar grid day (date).
            const s = new Date(l.startDate);
            const e = new Date(l.endDate);
            
            // Create timestamps using local components to match 'targetTime' exactly
            const startTime = new Date(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate()).getTime();
            const endTime = new Date(e.getUTCFullYear(), e.getUTCMonth(), e.getUTCDate()).getTime();
            
            return targetTime >= startTime && targetTime <= endTime;
        });
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const renderCalendar = () => {
        const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
        const days = [];
        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        // Add empty cells for days before month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} style={{ padding: '0.5rem' }} />);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const holiday = isHoliday(date);
            const leavesOnDay = hasLeave(date);
            const isToday = date.toDateString() === new Date().toDateString();

            days.push(
                <div
                    key={day}
                    style={{
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: '0.25rem',
                        minHeight: '80px',
                        background: holiday ? '#fef3c7' : leavesOnDay.length > 0 ? '#dbeafe' : 'white',
                        position: 'relative',
                        cursor: 'pointer'
                    }}
                    title={holiday ? holiday.name : leavesOnDay.map(l => `${l.user?.name || 'Leave'}: ${l.type}`).join(', ')}
                >
                    <div style={{
                        fontWeight: isToday ? 'bold' : 'normal',
                        color: isToday ? '#4f46e5' : '#374151',
                        marginBottom: '0.25rem'
                    }}>
                        {day}
                    </div>
                    {holiday && (
                        <div style={{
                            fontSize: '0.7rem',
                            color: '#92400e',
                            fontWeight: '600',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            🎉 {holiday.name}
                        </div>
                    )}
                    {leavesOnDay.slice(0, 2).map((leave, idx) => (
                        <div
                            key={idx}
                            style={{
                                fontSize: '0.7rem',
                                color: '#1e40af',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {showTeamLeaves ? `${leave.user?.name}: ${leave.type}` : leave.type}
                        </div>
                    ))}
                    {leavesOnDay.length > 2 && (
                        <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                            +{leavesOnDay.length - 2} more
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '0.25rem',
                    marginBottom: '0.5rem'
                }}>
                    {weekDays.map(day => (
                        <div
                            key={day}
                            style={{
                                padding: '0.5rem',
                                textAlign: 'center',
                                fontWeight: '600',
                                color: 'var(--text-muted)',
                                fontSize: '0.875rem'
                            }}
                        >
                            {day}
                        </div>
                    ))}
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '0.25rem'
                }}>
                    {days}
                </div>
            </div>
        );
    };

    return (
        <div className="glass-card" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <CalendarIcon size={24} />
                    {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={prevMonth}
                        className="btn btn-secondary"
                        style={{ padding: '0.5rem' }}
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <button
                        onClick={nextMonth}
                        className="btn btn-secondary"
                        style={{ padding: '0.5rem' }}
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Loading calendar...
                </div>
            ) : (
                <>
                    {renderCalendar()}

                    {/* Legend */}
                    <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.5rem', fontSize: '0.875rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ width: '20px', height: '20px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '0.25rem' }} />
                            <span>Holiday</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ width: '20px', height: '20px', background: '#dbeafe', border: '1px solid #3b82f6', borderRadius: '0.25rem' }} />
                            <span>Leave</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default HolidayCalendar;
